import {
  DismissedBannerDocument,
  useDismissedBannerQuery,
  useSetDismissedBannerMutation,
} from '../generated/graphql';

type BannerDismissal = {
  dismissed: boolean;
  dismiss: (() => void) | null;
};

/**
 * useBannerDismissal reports whether a server-persisted banner is dismissed for
 * the current viewer and exposes an action to dismiss it.
 *
 * Dismissal is keyed by `bannerId`; a null id means no banner, so `dismiss` is
 * null. Pass `resetAt` (epoch ms, e.g. a period boundary) to re-show the banner
 * once that boundary passes, or omit it for a permanent dismissal.
 */
export function useBannerDismissal(
  bannerId: string | null,
  {resetAt}: {resetAt?: number | null} = {}
): BannerDismissal {
  const {data, loading} = useDismissedBannerQuery({
    variables: {id: bannerId ?? ''},
    skip: bannerId == null,
  });
  const [setDismissedBanner] = useSetDismissedBannerMutation();

  const dismissedAt = data?.dismissedBanner?.dismissedAt;
  // Treat loading as dismissed so the banner never flashes before its
  // persisted state resolves.
  const dismissed =
    loading ||
    (dismissedAt != null &&
      (resetAt == null ||
        // A reset boundary in the future hasn't elapsed, so it can't have
        // invalidated this dismissal yet; honor it until the boundary passes.
        resetAt > Date.now() ||
        new Date(dismissedAt).getTime() >= resetAt));

  // No banner means nothing to dismiss, so there is no action to expose.
  let dismiss: (() => void) | null = null;
  if (bannerId != null) {
    const id = bannerId;
    dismiss = () => {
      void setDismissedBanner({
        variables: {id},
        // Write the dismissal into the cache up front so the banner hides
        // instantly; Apollo reverts it if the mutation fails.
        optimisticResponse: {
          __typename: 'Mutation',
          setDismissedBanner: {
            __typename: 'SetDismissedBannerPayload',
            success: true,
          },
        },
        update: cache => {
          cache.writeQuery({
            query: DismissedBannerDocument,
            variables: {id},
            data: {
              __typename: 'Query',
              dismissedBanner: {
                __typename: 'DismissedBanner',
                id,
                dismissedAt: new Date().toISOString(),
              },
            },
          });
        },
      }).catch(() => {
        // The optimistic write already reverted; nothing else to do.
      });
    };
  }

  return {dismissed, dismiss};
}
